import * as React from 'react';
import { createLogger } from '@ctrl-enter/tools/utils';

const logger = createLogger('web:component:system-settings-panel');
import { BarChart2, Joystick, Palette, Settings, Speaker } from 'lucide-react';
import {
  useShowContextBar,
  useUpdateUIPreferences,
  useUpdateTheme,
  useAutoScroll,
} from '../stores/system-settings-store';
import { useTheme } from '../styles/theme-provider';
import { cn } from '../utils/cn';
import { getPreferredVoice, setPreferredVoice } from '../hooks/use-openai-tts';
import { InfoSection } from './info-section';
import type { TTSVoice } from '@ctrl-enter/types';

type ThemeOption = {
  value: 'light' | 'dark' | 'system';
  label: string;
  description: string;
  icon: string;
};

type VoiceOption = {
  value: TTSVoice;
  label: string;
  description: string;
};

const themeOptions: ThemeOption[] = [
  {
    value: 'light',
    label: 'Light',
    description: 'Clean and bright interface',
    icon: '☀️',
  },
  {
    value: 'dark',
    label: 'Dark',
    description: 'Easy on the eyes in low light',
    icon: '🌙',
  },
  {
    value: 'system',
    label: 'System',
    description: 'Matches your system preference',
    icon: '💻',
  },
];

const voiceOptions: VoiceOption[] = [
  { value: 'alloy', label: 'Alloy', description: 'Neutral and balanced voice' },
  { value: 'echo', label: 'Echo', description: 'Male voice, clear and articulate' },
  { value: 'fable', label: 'Fable', description: 'British accent, expressive' },
  { value: 'onyx', label: 'Onyx', description: 'Deep male voice, authoritative' },
  { value: 'nova', label: 'Nova', description: 'Female voice, friendly and warm' },
  { value: 'shimmer', label: 'Shimmer', description: 'Female voice, soft and soothing' },
];

export type SystemSettingsPanelProps = {
  className?: string;
};

export const SystemSettingsPanel = ({ className }: SystemSettingsPanelProps) => {
  const { theme: currentTheme, resolvedTheme } = useTheme();
  const updateTheme = useUpdateTheme();
  const updateUIPreferences = useUpdateUIPreferences();
  const showContextBar = useShowContextBar();
  const autoScroll = useAutoScroll();
  const [selectedVoice, setSelectedVoice] = React.useState<TTSVoice>(getPreferredVoice);

  const handleThemeChange = (newTheme: 'light' | 'dark' | 'system') => {
    updateTheme(newTheme);
  };

  const handleVoiceChange = React.useCallback((voice: TTSVoice) => {
    setSelectedVoice(voice);
    setPreferredVoice(voice);
  }, []);

  logger.info('SystemSettingsPanel rendered');

  return (
    <div className={cn('w-full space-y-6', className)}>
      <InfoSection title="App Settings" titleIcon={<Settings className="w-5 h-5" />}>
        <p className="text-secondary-enhanced -mt-2">Configure how the app looks and behaves.</p>
      </InfoSection>

      {/* Theme Section */}
      <InfoSection title="Theme" titleIcon={<Palette className="w-5 h-5" />}>
        <p className="text-secondary-enhanced text-sm -mt-2 mb-3">
          Choose how the app looks. System theme will follow your operating system&apos;s
          preference.
          {currentTheme === 'system' && (
            <span className="block mt-1">Currently using: {resolvedTheme} (from system)</span>
          )}
        </p>
        <div className="grid grid-cols-1 gap-3">
          {themeOptions.map(option => (
            <button
              key={option.value}
              className={cn(
                'w-full justify-start h-auto p-4 text-left rounded-xl transition-all duration-300',
                currentTheme === option.value
                  ? 'border-2 border-primary glass-surface-active shadow-md'
                  : 'glass-surface hover:glass-surface-hover'
              )}
              onClick={() => handleThemeChange(option.value)}
            >
              <div className="flex items-center gap-3">
                <span className="text-xl" role="img" aria-label={option.label}>
                  {option.icon}
                </span>
                <div className="flex flex-col">
                  <span className="font-semibold">{option.label}</span>
                  <span className="text-secondary-enhanced text-sm font-normal">
                    {option.description}
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </InfoSection>

      {/* App behavior Section */}
      <InfoSection title="App Behavior" titleIcon={<Joystick className="w-5 h-5" />}>
        <p className="text-secondary-enhanced text-sm -mt-2 mb-3">Control how the app behaves.</p>
        <label className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border bg-background cursor-pointer hover:bg-accent/5 transition-colors">
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-semibold">Toggle auto-scroll</span>
            <span className="text-secondary-enhanced text-xs font-normal">
              Automatically scroll to the bottom of the chat when a new message is added
            </span>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={autoScroll}
            onClick={() => updateUIPreferences({ autoScroll: !autoScroll })}
            className={cn(
              'relative inline-flex flex-shrink-0 items-center',
              'w-11 h-6 rounded-full',
              'transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
              autoScroll ? 'bg-primary' : 'bg-border'
            )}
          >
            <span
              className={cn(
                'inline-block w-5 h-5 rounded-full bg-white shadow-sm',
                'transition-transform duration-200',
                autoScroll ? 'translate-x-[22px]' : 'translate-x-[2px]'
              )}
            />
          </button>
        </label>
      </InfoSection>

      {/* Display Section */}
      <InfoSection title="Display" titleIcon={<BarChart2 className="w-5 h-5" />}>
        <p className="text-secondary-enhanced text-sm -mt-2 mb-3">
          Control what information is shown in the chat interface.
        </p>
        <label className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border bg-background cursor-pointer hover:bg-accent/5 transition-colors">
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-semibold">Show context / token bar</span>
            <span className="text-secondary-enhanced text-xs font-normal">
              Display token usage beneath the chat header
            </span>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={showContextBar}
            onClick={() => updateUIPreferences({ showContextBar: !showContextBar })}
            className={cn(
              'relative inline-flex flex-shrink-0 items-center',
              'w-11 h-6 rounded-full',
              'transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
              showContextBar ? 'bg-primary' : 'bg-border'
            )}
          >
            <span
              className={cn(
                'inline-block w-5 h-5 rounded-full bg-white shadow-sm',
                'transition-transform duration-200',
                showContextBar ? 'translate-x-[22px]' : 'translate-x-[2px]'
              )}
            />
          </button>
        </label>
      </InfoSection>

      {/* Voice Section */}
      <InfoSection title="Voice" titleIcon={<Speaker className="w-5 h-5" />}>
        <p className="text-secondary-enhanced text-sm -mt-2 mb-3">Choose how the app speaks.</p>
        <div className="flex flex-col gap-2">
          {voiceOptions.map(option => (
            <button
              key={option.value}
              onClick={() => handleVoiceChange(option.value)}
              className={cn(
                'flex items-center gap-3 p-3 rounded-lg border-2 transition-all',
                'hover:bg-accent/5',
                selectedVoice === option.value
                  ? 'border-primary bg-primary/5'
                  : 'border-border bg-background'
              )}
            >
              <div
                className={cn(
                  'flex-shrink-0 w-4 h-4 rounded-full border-2 transition-all',
                  selectedVoice === option.value
                    ? 'border-primary bg-primary'
                    : 'border-border bg-background'
                )}
              >
                {selectedVoice === option.value && (
                  <div className="w-full h-full rounded-full bg-white scale-50" />
                )}
              </div>
              <div className="flex flex-col items-start gap-0.5 text-left">
                <span className="font-semibold">{option.label}</span>
                <span className="text-secondary-enhanced text-sm font-normal">
                  {option.description}
                </span>
              </div>
            </button>
          ))}
        </div>
      </InfoSection>
    </div>
  );
};
